import { useEffect, useState } from 'react';
import { formatMoney } from '../../utils/currency';

function formatLeft(seconds) {
  const s = Math.max(0, Number(seconds) || 0);
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${String(m).padStart(2, '0')}:${String(r).padStart(2, '0')}`;
}

function ReceivedIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 64 64" width="56" height="56" aria-hidden="true">
      <circle cx="32" cy="32" r="30" fill="none" stroke="currentColor" strokeWidth="2.5" opacity="0.35" />
      <circle cx="32" cy="32" r="22" fill="currentColor" opacity="0.12" />
      <path
        d="M20.5 32.5l7.2 7.2 15.8-16.4"
        fill="none"
        stroke="currentColor"
        strokeWidth="3.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export const CASHIER_RETURN_PROVIDERS = [
  'payurus',
  'payferrari',
  'havalespeed',
  'maxhavale',
  'havale',
];

function buildReturnHints(flag, statusHint) {
  return {
    hintedSuccess: flag === 'success'
      || statusHint === 'success'
      || statusHint === 'completed'
      || statusHint === 'paid'
      || statusHint === 'approved',
    hintedFail: flag === 'failed'
      || flag === 'fail'
      || statusHint === 'failed'
      || statusHint === 'cancelled'
      || statusHint === 'canceled'
      || statusHint === 'rejected',
  };
}

export function parseCashierReturnParams(
  search = typeof window !== 'undefined' ? window.location.search : '',
  providers = CASHIER_RETURN_PROVIDERS,
) {
  const params = new URLSearchParams(search);
  const statusHint = String(params.get('status') || '').trim().toLowerCase();
  const requestRaw = params.get('request');
  const requestId = requestRaw != null && String(requestRaw).trim() !== ''
    ? Number(requestRaw)
    : null;
  const normalizedId = Number.isFinite(requestId) && requestId > 0 ? requestId : null;
  for (const provider of providers) {
    const flag = String(params.get(provider) || '').trim().toLowerCase();
    if (!flag) continue;
    return {
      provider,
      flag,
      requestId: normalizedId,
      ...buildReturnHints(flag, statusHint),
    };
  }
  return null;
}

export function parsePayurusReturnParams(search = typeof window !== 'undefined' ? window.location.search : '') {
  const parsed = parseCashierReturnParams(search, CASHIER_RETURN_PROVIDERS);
  if (!parsed) return null;
  return parsed;
}

export function clearCashierReturnParams(providers = CASHIER_RETURN_PROVIDERS) {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  for (const provider of providers) {
    url.searchParams.delete(provider);
  }
  url.searchParams.delete('request');
  url.searchParams.delete('status');
  const next = `${url.pathname}${url.searchParams.toString() ? `?${url.searchParams.toString()}` : ''}${url.hash || ''}`;
  window.history.replaceState(window.history.state, '', next);
}

export function clearPayurusReturnParams() {
  clearCashierReturnParams();
}

export function setCashierReturnParams(provider, { requestId, flag = 'return', status } = {}) {
  if (typeof window === 'undefined' || !provider) return;
  const url = new URL(window.location.href);
  for (const key of CASHIER_RETURN_PROVIDERS) {
    if (key !== provider) url.searchParams.delete(key);
  }
  url.searchParams.set('tab', 'deposit');
  url.searchParams.set('provider', String(provider));
  url.searchParams.set(String(provider), String(flag || 'return'));
  if (requestId != null && String(requestId).trim() !== '') {
    url.searchParams.set('request', String(requestId));
  } else {
    url.searchParams.delete('request');
  }
  if (status != null && String(status).trim() !== '') {
    url.searchParams.set('status', String(status));
  } else {
    url.searchParams.delete('status');
  }
  const next = `${url.pathname}?${url.searchParams.toString()}${url.hash || ''}`;
  window.history.replaceState(window.history.state, '', next);
}

export function openPayurusPaymentUrl(url) {
  const href = String(url || '').trim();
  if (!href || typeof window === 'undefined') return false;
  try {
    const opened = window.open(href, '_blank', 'noopener,noreferrer');
    if (opened) return true;
  } catch {
  }
  window.location.assign(href);
  return true;
}

export function cashierProviderLabel(translateFn, provider) {
  const key = String(provider || 'payurus').toLowerCase();
  const labelKey = `account.payments.method.${key}`;
  const label = typeof translateFn === 'function' ? translateFn(labelKey) : labelKey;
  if (label && label !== labelKey) return label;
  if (key === 'havalespeed') return 'Speed Havale';
  if (key === 'maxhavale') return 'Max Havale';
  if (key === 'havale') return 'Havale';
  if (key === 'direct_crypto' || key === 'crypto') return 'Kripto';
  return 'PayUrus';
}

export function PayurusReturnPanel({
  phase,
  request,
  tr: translateProp,
  t: translateLegacy,
  variant = 'desktop',
  provider = 'payurus',
  onContinue,
  onGoRequests,
}) {
  const tr = typeof translateProp === 'function'
    ? translateProp
    : (typeof translateLegacy === 'function' ? translateLegacy : (key) => key);
  const root = variant === 'mobile' ? 'mobile-account-cashier__havale-active' : 'payments-havale-active';
  const received = variant === 'mobile' ? 'mobile-account-cashier__havale-received' : 'payments-havale-active__received';
  const receivedIcon = variant === 'mobile' ? 'mobile-account-cashier__havale-received-icon' : 'payments-havale-active__received-icon';
  const result = variant === 'mobile' ? 'mobile-account-cashier__havale-result' : 'payments-havale-active__result';
  const btn = variant === 'mobile' ? 'mobile-account-cashier__notice-btn' : 'payments-form__quick-btn';
  const amount = request?.amount != null ? formatMoney(request.amount) : null;
  const providerName = cashierProviderLabel(tr, provider);
  const providerParams = { provider: providerName };

  if (phase === 'completed') {
    const successTitle = (provider === 'direct_crypto' || provider === 'crypto')
      ? tr('account.payments.status_approved')
      : tr('account.payments.payurus.depositSuccess', providerParams);
    return (
      <div className={root}>
        <div className={received}>
          <ReceivedIcon className={receivedIcon} />
          <strong>{successTitle}</strong>
          <p>
            {amount
              ? tr('account.payments.payurus.depositSuccessHint', { amount, ...providerParams })
              : tr('account.payments.payurus.depositSuccessHintGeneric', providerParams)}
          </p>
          <button type="button" className={`${btn} is-primary`} onClick={onContinue}>
            {tr('account.payments.payurus.continue')}
          </button>
          {onGoRequests ? (
            <button type="button" className={btn} onClick={onGoRequests}>
              {tr('account.payments.goToRequests')}
            </button>
          ) : null}
        </div>
      </div>
    );
  }

  if (phase === 'failed') {
    return (
      <div className={root}>
        <div className={`${result} is-rejected`}>
          <strong>{tr('account.payments.payurus.depositFailed', providerParams)}</strong>
          <p>{tr('account.payments.payurus.depositFailedHint', providerParams)}</p>
          <button type="button" className={`${btn} is-primary`} onClick={onContinue}>
            {tr('account.payments.havale.tryAgain')}
          </button>
        </div>
      </div>
    );
  }

  if (phase === 'expired') {
    return (
      <div className={root}>
        <div className={`${result} is-rejected`}>
          <strong>{tr('account.payments.havale.resultExpired', providerParams)}</strong>
          <p>{tr('account.payments.havale.resultExpiredHint', providerParams)}</p>
          <button type="button" className={`${btn} is-primary`} onClick={onContinue}>
            {tr('account.payments.havale.tryAgain')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={root}>
      <div className={received}>
        <strong>{tr('account.payments.payurus.depositProcessing', providerParams)}</strong>
        <p>{tr('account.payments.payurus.depositProcessingHint', providerParams)}</p>
        {amount ? (
          <p style={{ marginTop: 8 }}>
            <strong>{amount}</strong>
          </p>
        ) : null}
      </div>
    </div>
  );
}

export function ActivePayurusPanel({
  request,
  tr: translateProp,
  t: translateLegacy,
  variant = 'desktop',
  onGoRequests,
  onCancel,
  cancelling = false,
}) {
  const tr = typeof translateProp === 'function'
    ? translateProp
    : (typeof translateLegacy === 'function' ? translateLegacy : (key) => key);
  const [left, setLeft] = useState(request?.remaining_seconds ?? 0);
  const root = variant === 'mobile' ? 'mobile-account-cashier__havale-active' : 'payments-havale-active';
  const title = variant === 'mobile' ? 'mobile-account-cashier__havale-title' : 'payments-havale-active__title';
  const row = variant === 'mobile' ? 'mobile-account-cashier__havale-row' : 'payments-havale-active__row';
  const timer = variant === 'mobile' ? 'mobile-account-cashier__havale-timer' : 'payments-havale-active__timer';
  const primaryBtn = variant === 'mobile' ? 'mobile-account-cashier__submit' : 'payments-form__submit';
  const secondaryBtn = variant === 'mobile' ? 'mobile-account-cashier__notice-btn' : 'payments-form__quick-btn';
  const paymentUrl = request?.redirect_url || null;
  const confirmed = Boolean(
    request?.confirm_status === 'notified'
    || request?.deposit_confirmed
    || request?.user_confirmed,
  );

  useEffect(() => {
    setLeft(request?.remaining_seconds ?? 0);
  }, [request?.id, request?.remaining_seconds]);

  useEffect(() => {
    if (!request?.id || request?.type === 'withdraw') return undefined;
    const id = setInterval(() => setLeft((v) => Math.max(0, v - 1)), 1000);
    return () => clearInterval(id);
  }, [request?.id, request?.type]);

  if (!request) return null;

  if (request.type === 'withdraw') {
    return (
      <div className={root}>
        <h3 className={title}>{tr('account.payments.payurus.withdrawPending')}</h3>
        <div className={row}>
          <span>{tr('account.payments.havale.amountDue')}</span>
          <strong>{formatMoney(request.amount)}</strong>
        </div>
        {request.wallet_id ? (
          <div className={row}>
            <span>{tr('account.payments.payurus.walletId')}</span>
            <strong>{request.wallet_id}</strong>
          </div>
        ) : null}
        {onGoRequests ? (
          <button type="button" className={secondaryBtn} onClick={onGoRequests}>
            {tr('account.payments.requests')}
          </button>
        ) : null}
      </div>
    );
  }

  return (
    <div className={root}>
      <div className={timer}>
        <span>{tr('account.payments.havale.countdown')}</span>
        <strong>{formatLeft(left)}</strong>
      </div>
      <h3 className={title}>{tr('account.payments.payurus.depositPending')}</h3>
      <div className={row}>
        <span>{tr('account.payments.havale.amountDue')}</span>
        <strong>{formatMoney(request.amount)}</strong>
      </div>
      {paymentUrl ? (
        <button
          type="button"
          className={primaryBtn}
          style={{ marginTop: 12 }}
          onClick={() => openPayurusPaymentUrl(paymentUrl)}
        >
          {tr('account.payments.payurus.reopenPayment')}
        </button>
      ) : (
        <p style={{ marginTop: 12 }}>
          {tr('account.payments.payurus.urlMissing')}
        </p>
      )}
      {!confirmed && onCancel ? (
        <button
          type="button"
          className={secondaryBtn}
          style={{ marginTop: 10 }}
          disabled={cancelling}
          onClick={onCancel}
        >
          {cancelling ? tr('account.payments.payurus.cancelling') : tr('account.payments.payurus.cancelDeposit')}
        </button>
      ) : null}
    </div>
  );
}
